import { Injectable } from '@angular/core';
import { CollectionPaths } from '@models/collections';
import {
	GlobalFormsKeywords,
	GlobalGenericFormTemplate,
} from '@models/global-forms';
import { Selector, State } from '@ngxs/store';
import {
	FirestoreStateBaseForGlobalWithSubscription,
	GlobalStateModel,
} from '@state/FirestoreStateBaseForGlobal';
import { DEFAULT_STATE_BASE } from '@state/FirestoreStateBase';
import { where } from '@angular/fire/firestore';
import { MatterFileType } from '@models/matter';
import { SelectedMatter } from '@state/matter/matter.selectors';

type GenericFormTemplatesStateModelItem = GlobalGenericFormTemplate[];

export interface GenericFormTemplatesStateModel extends GlobalStateModel {
	items: GenericFormTemplatesStateModelItem;
}

const DEFAULT_STATE: GenericFormTemplatesStateModel = {
	...DEFAULT_STATE_BASE,
	items: [],
};

@State<GenericFormTemplatesStateModel>({
	name: 'genericFormsTemplates',
	defaults: DEFAULT_STATE,
})
@Injectable()
export class GenericFormsTemplatesState extends FirestoreStateBaseForGlobalWithSubscription<
	GlobalGenericFormTemplate,
	GenericFormTemplatesStateModel
> {
	constructor() {
		super(
			() => CollectionPaths.global_forms(),
			(payload) => ({ items: payload as GenericFormTemplatesStateModelItem }),
			() => DEFAULT_STATE,
			() => [where(`keywords.${GlobalFormsKeywords.GENERIC}`, '==', true)],
		);
	}

	@Selector([GenericFormsTemplatesState])
	static getGenericFormTemplates(
		state: GenericFormTemplatesStateModel,
	): GlobalGenericFormTemplate[] {
		return state.items;
	}

	@Selector([GenericFormsTemplatesState.getGenericFormByMatterType])
	static mapGenericFormsByFormType(
		state: GlobalGenericFormTemplate[],
	): Record<string, GlobalGenericFormTemplate[]> {
		return state.reduce(
			(acc, template) => {
				const label = template.labels.formType;
				if (!acc[label]) {
					acc[label] = [];
				}
				acc[label].push(template);
				return acc;
			},
			{} as Record<string, GlobalGenericFormTemplate[]>,
		);
	}

	@Selector([
		GenericFormsTemplatesState.getGenericFormTemplates,
		SelectedMatter.matterFileType,
	])
	static getGenericFormByMatterType(
		state: GlobalGenericFormTemplate[],
		matterType: MatterFileType,
	): GlobalGenericFormTemplate[] {
		return matterType
			? state.filter((template) =>
					template.labels.matterTypes.includes(matterType),
				)
			: [];
	}
}
